import { SOSSIETY_RED, SOSSIETY_RED_HOVER } from '@/styles/brand';
import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { ArrowLeft } from 'lucide-react'
import { useI18n } from '@/contexts/I18nContext'
import { useAgendaConfig } from '@/features/calendar/hooks/useAgendaConfig'
import { TopNavigation } from '@/components/layout/TopNavigation'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'

const C = {
  bg:      '#160C0C',
  surface: '#281B1B',
  border:  'rgba(255,255,255,0.15)',
  muted:   '#8D8D8D',
  accent:  SOSSIETY_RED,
}

const CURRENCIES = ['BRL', 'USD', 'EUR']

const Pricing = () => {
  const navigate = useNavigate()
  const { t }    = useI18n()

  const { config, isLoading, updateConfig, isUpdating } = useAgendaConfig()

  const [classValue, setClassValue] = useState('')
  const [currency,   setCurrency]   = useState('USD')
  const [synced,     setSynced]     = useState(false)
  const [saved,      setSaved]      = useState(false)

  // Sync from fetched config once
  if (!synced && !isLoading && config) {
    setClassValue(config.class_value > 0 ? String(config.class_value) : '')
    setCurrency(config.currency ?? 'USD')
    setSynced(true)
  }

  const handleSave = async () => {
    await updateConfig({
      class_value: parseFloat(classValue) || 0,
      currency,
    })
    setSaved(true)
    setTimeout(() => setSaved(false), 2000)
  }

  return (
    <div className="min-h-screen text-white" style={{ backgroundColor: C.bg }}>
      <TopNavigation />

      <div className="pt-16">
        {/* Header */}
        <div className="relative flex items-center px-4 py-3.5 border-b border-[#281B1B]">
          <button
            onClick={() => navigate(-1)}
            className="p-1 text-white/80 hover:text-white transition-colors z-10">
            <ArrowLeft className="w-5 h-5" />
          </button>
          <h1 className="absolute left-1/2 -translate-x-1/2 text-base font-semibold truncate max-w-[60%]">
            {t('instructor_tools.edit_pricing')}
          </h1>
        </div>

        <div className="px-5 pt-6">
          <p className="text-sm font-medium text-white mb-1">
            {t('instructor_tools.class_value')}
          </p>
          <p className="text-xs mb-5" style={{ color: C.muted }}>
            {t('instructor_tools.class_value_subtitle')}
          </p>

          <div className="flex gap-3">
            {/* Price input */}
            <input
              type="number"
              min="0"
              step="0.01"
              inputMode="decimal"
              placeholder="0.00"
              value={classValue}
              onChange={e => setClassValue(e.target.value)}
              className="flex-1 rounded-xl px-4 py-3 text-sm text-white border focus:outline-none focus:ring-1 focus:ring-sossiety-red"
              style={{ backgroundColor: C.surface, borderColor: C.border }}
            />

            {/* Currency select — dark style matching signup/modals */}
            <Select value={currency} onValueChange={setCurrency}>
              <SelectTrigger
                className="min-w-[5.75rem] rounded-xl border px-3 focus:outline-none focus:ring-1 focus:ring-sossiety-red [&_svg]:ml-1 [&_svg]:shrink-0 [&_svg]:text-white/60"
                style={{
                  backgroundColor: C.surface,
                  borderColor: C.border,
                  color: '#fff',
                  fontSize: '14px',
                }}
              >
                <SelectValue />
              </SelectTrigger>
              <SelectContent
                className="w-[var(--radix-select-trigger-width)] min-w-[var(--radix-select-trigger-width)] bg-black border-0 p-0 [&_[data-radix-select-viewport]]:px-2 [&_[data-radix-select-viewport]]:py-2"
              >
                {CURRENCIES.map(c => (
                  <SelectItem
                    key={c}
                    value={c}
                    className="text-white text-sm !pl-3 !pr-3 py-2.5 focus:bg-white/10 focus:text-white [&>span:first-child]:hidden"
                  >
                    {c}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <button
            onClick={handleSave}
            disabled={isUpdating}
            className="w-full mt-6 rounded-full py-3 text-sm font-semibold text-white disabled:opacity-40 transition-opacity"
            style={{ backgroundColor: C.accent }}
          >
            {isUpdating
              ? t('instructor_tools.saving')
              : saved
                ? t('instructor_tools.saved')
                : t('instructor_tools.save_config')}
          </button>
        </div>
      </div>
    </div>
  )
}

export default Pricing
